import { supabase } from '../../lib/supabaseClient';
import type { Database } from '../../types/database.types';
import { validateLayoutTokens, DEFAULT_LAYOUT_TOKENS, type PdfLayoutTokens } from './pdfLayoutTokens';

export type PdfLayoutRow = Database['public']['Tables']['document_pdf_layouts']['Row'];

export async function listPdfLayouts(): Promise<PdfLayoutRow[]> {
  const { data, error } = await supabase.from('document_pdf_layouts')
    .select('*').order('display_order');
  if (error) throw error;
  return data ?? [];
}

export async function setDefaultPdfLayout(id: string): Promise<void> {
  // 2 updates non transactionnels (contrainte : index unique partiel sur is_default,
  // impossible de poser le nouveau défaut avant d'avoir retiré l'ancien). Si le set
  // échoue, on restaure l'ancien défaut en best-effort ; dans le pire cas résiduel
  // (aucun défaut en base), le rendu retombe sur DEFAULT_LAYOUT_TOKENS via
  // resolveLayoutTokens et un nouveau clic répare l'état.
  const { data: prev } = await supabase.from('document_pdf_layouts')
    .select('id').eq('is_default', true).neq('id', id).maybeSingle();
  const { error: unsetErr } = await supabase.from('document_pdf_layouts')
    .update({ is_default: false, updated_at: new Date().toISOString() }).eq('is_default', true).neq('id', id);
  if (unsetErr) throw unsetErr;
  const { error } = await supabase.from('document_pdf_layouts')
    .update({ is_default: true, updated_at: new Date().toISOString() }).eq('id', id);
  if (error) {
    if (prev) {
      await supabase.from('document_pdf_layouts')
        .update({ is_default: true, updated_at: new Date().toISOString() }).eq('id', prev.id);
    }
    throw error;
  }
}

export async function applyPdfLayoutTokens(id: string, current: unknown, proposed: PdfLayoutTokens): Promise<void> {
  const { error } = await supabase.from('document_pdf_layouts')
    .update({
      previous_tokens: current as never,
      tokens: proposed as never,
      updated_at: new Date().toISOString(),
    }).eq('id', id);
  if (error) throw error;
}

export async function revertPdfLayoutTokens(row: PdfLayoutRow): Promise<void> {
  if (!row.previous_tokens) throw new Error('Aucune version précédente à rétablir.');
  const { error } = await supabase.from('document_pdf_layouts')
    .update({
      tokens: row.previous_tokens,
      previous_tokens: row.tokens,
      updated_at: new Date().toISOString(),
    }).eq('id', row.id);
  if (error) throw error;
}

export async function proposePdfLayoutModification(layoutId: string, instruction: string): Promise<PdfLayoutTokens> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Session expirée');
  const { data, error } = await supabase.functions.invoke('modify-pdf-layout', {
    body: { layout_id: layoutId, instruction },
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (error) throw new Error('La modification a échoué.');
  if (data && (data as { error?: string }).error) throw new Error(String((data as { error: string }).error));
  const proposed = (data as { proposed_tokens?: unknown }).proposed_tokens;
  const validated = validateLayoutTokens(proposed);
  if (!validated.ok) throw new Error('Réponse de l’IA invalide, réessayez.');
  return validated.tokens;
}

/** Tokens sûrs pour le rendu : ligne validée, sinon défaut Latéral. */
export function resolveLayoutTokens(row: PdfLayoutRow | undefined): PdfLayoutTokens {
  if (!row) return DEFAULT_LAYOUT_TOKENS;
  const v = validateLayoutTokens(row.tokens);
  return v.ok ? v.tokens : DEFAULT_LAYOUT_TOKENS;
}
